import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Autocomplete,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  FilterAltOff,
  CheckCircle,
  ErrorOutline,
  OpenInNew,
  Link as LinkIcon,
  Search,
  TouchApp,
} from '@mui/icons-material';
import {
  referralLinkAPI,
  contactsAPI,
  eventsAPI,
  promotionsAPI,
  accommodationAPI,
  citiesAPI,
  categoriesAPI,
} from '../../services/api';
import browserGlobal from '../../utils/browserGlobal';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';

const MODULE_OPTIONS = ['Contact', 'Event', 'Promotion', 'Accomodation'];

const ReferralLink = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser?.role || '');

  // UI loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingEntities, setIsFetchingEntities] = useState(false);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);

  // Main Referral Links list
  const [referralLinks, setReferralLinks] = useState([]);

  // Filter Bar state
  const [filterModuleKey, setFilterModuleKey] = useState('Contact');
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown options lists
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [entities, setEntities] = useState([]);

  // Selection states for Dialog form
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [formSelectedEntity, setFormSelectedEntity] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    moduleKey: 'Contact',
    entityId: '',
    url: '',
    label: '',
    isActive: true,
  });

  const [errors, setErrors] = useState({
    moduleKey: '',
    entityId: '',
    url: '',
    label: '',
  });

  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  /* ------------------- Initial Load: Cities ------------------- */
  const fetchCities = useCallback(async () => {
    try {
      const res = await citiesAPI.getCities();
      if (res?.data?.success && Array.isArray(res.data.payload)) {
        const list = res.data.payload;
        setCities(list);
        if (list.length > 0 && !selectedCityId) {
          setSelectedCityId(list[0].id);
        }
      }
    } catch (e) {
      console.error('Error fetching cities:', e);
    }
  }, [selectedCityId]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  /* ------------------- Load Categories (for Contact Module) ------------------- */
  const fetchCategories = useCallback(async (cityId) => {
    if (!cityId) return;
    setIsFetchingCategories(true);
    try {
      const res = await categoriesAPI.getCategories(cityId, 0, 100, { hasContact: true });
      if (res?.data?.success && Array.isArray(res.data.payload)) {
        setCategories(res.data.payload);
      } else {
        setCategories([]);
      }
    } catch (e) {
      console.error('Error fetching categories:', e);
      setCategories([]);
    } finally {
      setIsFetchingCategories(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCityId && formData.moduleKey === 'Contact') {
      fetchCategories(selectedCityId);
    }
  }, [selectedCityId, formData.moduleKey, fetchCategories]);

  /* ------------------- Load Entities for Current Module ------------------- */
  const fetchModuleEntities = useCallback(
    async (moduleKey, cityId, categoryId = '') => {
      setIsFetchingEntities(true);
      try {
        let list = [];
        let res;
        const currentCity = cityId || (cities[0]?.id ?? 1);

        if (moduleKey === 'Contact') {
          res = await contactsAPI.getContacts(currentCity, categoryId || null, 0, 100);
          list = res?.data?.payload || [];
        } else if (moduleKey === 'Event') {
          res = await eventsAPI.getEvents(currentCity, 0, 100);
          list = res?.data?.payload || [];
        } else if (moduleKey === 'Promotion') {
          res = await promotionsAPI.getPromotions(currentCity, 0, 100);
          list = res?.data?.payload || [];
        } else if (moduleKey === 'Accomodation') {
          res = await accommodationAPI.getAccommodations(currentCity, 0, 100);
          list = res?.data?.payload || [];
        }

        const validList = Array.isArray(list) ? list : [];
        setEntities(validList);
        return validList;
      } catch (err) {
        console.error(`Error fetching entities for module ${moduleKey}:`, err);
        setEntities([]);
        return [];
      } finally {
        setIsFetchingEntities(false);
      }
    },
    [cities]
  );

  useEffect(() => {
    fetchModuleEntities(formData.moduleKey, selectedCityId, selectedCategoryId);
  }, [formData.moduleKey, selectedCityId, selectedCategoryId, fetchModuleEntities]);

  /* ------------------- Fetch Referral Link(s) ------------------- */
  const fetchReferralLinks = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await referralLinkAPI.getReferralLink(filterModuleKey);
      if (res?.data?.success && res.data.payload) {
        const payload = res.data.payload;
        const list = Array.isArray(payload) ? payload : [payload];
        setReferralLinks(list);
      } else {
        // Fallback: batch fetch for known module entity items
        if (entities.length > 0) {
          const promises = entities.slice(0, 30).map((ent) =>
            referralLinkAPI
              .getReferralLink(filterModuleKey, ent.id)
              .then((r) => (r?.data?.success && r.data.payload ? r.data.payload : null))
              .catch(() => null)
          );
          const results = await Promise.all(promises);
          const valid = results.filter(Boolean);
          const flattened = valid.flatMap((item) => (Array.isArray(item) ? item : [item]));
          setReferralLinks(flattened);
        } else {
          setReferralLinks([]);
        }
      }
    } catch (e) {
      console.error('Error fetching referral link:', e);
      setReferralLinks([]);
    } finally {
      setIsLoading(false);
    }
  }, [filterModuleKey, entities]);

  useEffect(() => {
    fetchReferralLinks();
  }, [fetchReferralLinks]);

  /* ---------------- Map Entity Details for Display ---------------- */
  const entityMap = useMemo(() => {
    const map = new Map();
    entities.forEach((ent) => {
      map.set(ent.id, ent.title || ent.name || ent.contactPerson || `Entity #${ent.id}`);
    });
    return map;
  }, [entities]);

  const getEntityTitle = (entityId) => {
    if (entityMap.has(entityId)) {
      return entityMap.get(entityId);
    }
    return `ID: ${entityId}`;
  };

  /* ---------------- Search Filtered Items ---------------- */
  const filteredReferralLinks = useMemo(() => {
    let result = referralLinks;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (item) =>
          String(item.label || '').toLowerCase().includes(q) ||
          String(item.url || '').toLowerCase().includes(q) ||
          String(item.entityId || '').includes(q) ||
          getEntityTitle(item.entityId).toLowerCase().includes(q)
      );
    }
    return result;
  }, [referralLinks, searchQuery, entityMap]);

  /* ---------------- UI Event Handlers ---------------- */
  const handleClearFilters = () => {
    setFilterModuleKey('Contact');
    setSearchQuery('');
  };

  const handleOpenDialog = async (item = null) => {
    setErrors({ moduleKey: '', entityId: '', url: '', label: '', summary: '' });

    const targetModule = item ? item.moduleKey : filterModuleKey;
    const currentCity = selectedCityId || (cities[0]?.id ?? 1);

    const itemCatId = item
      ? String(item.categoryId ?? item.CategoryId ?? item.categoryID ?? '')
      : '';
    const activeCatId = item ? itemCatId : selectedCategoryId;
    if (item) {
      setSelectedCategoryId(itemCatId);
    }

    const loadedEntities = await fetchModuleEntities(targetModule, currentCity, activeCatId);

    if (item) {
      setSelectedItem(item);
      const matched = loadedEntities.find((e) => Number(e.id) === Number(item.entityId));
      const fallbackEntity = matched || (item.entityId ? {
        id: item.entityId,
        title: getEntityTitle(item.entityId) || `ID: ${item.entityId}`
      } : null);
      setFormSelectedEntity(fallbackEntity);
      setFormData({
        moduleKey: item.moduleKey || 'Contact',
        entityId: item.entityId != null ? String(item.entityId) : '',
        url: item.url || '',
        label: item.label || '',
        summary: item.summary || item.Summary || '',
        isActive: item.isActive ?? true,
        categoryId: itemCatId,
      });
    } else {
      setSelectedItem(null);
      setFormSelectedEntity(null);
      setFormData({
        moduleKey: targetModule,
        entityId: '',
        url: '',
        label: '',
        summary: '',
        isActive: true,
        categoryId: selectedCategoryId || '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setFormSelectedEntity(null);
    setErrors({ moduleKey: '', entityId: '', url: '', label: '', summary: '' });
  };

  const handleModuleChange = (e) => {
    const newMod = e.target.value;
    setFormData((prev) => ({ ...prev, moduleKey: newMod, entityId: '' }));
    setFormSelectedEntity(null);
    setSelectedCategoryId('');
    fetchModuleEntities(newMod, selectedCityId, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const validateForm = () => {
    const nextErrors = { moduleKey: '', entityId: '', url: '', label: '', summary: '' };
    let hasError = false;

    if (!formData.moduleKey) {
      nextErrors.moduleKey = 'Module is required';
      hasError = true;
    }
    if (!formData.entityId || Number(formData.entityId) <= 0) {
      nextErrors.entityId = 'Please select a Title to assign entity ID';
      hasError = true;
    }
    if (!formData.label?.trim()) {
      nextErrors.label = 'Label is required';
      hasError = true;
    }
    if (formData.summary && formData.summary.length > 50) {
      nextErrors.summary = 'Summary cannot exceed 50 characters';
      hasError = true;
    }
    if (!formData.url?.trim()) {
      nextErrors.url = 'URL is required';
      hasError = true;
    } else {
      try {
        new URL(formData.url);
      } catch (e) {
        nextErrors.url = 'Please enter a valid URL (e.g., https://example.com)';
        hasError = true;
      }
    }

    setErrors(nextErrors);
    return !hasError;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    const payload = {
      moduleKey: formData.moduleKey,
      entityId: Number(formData.entityId),
      url: formData.url.trim(),
      label: formData.label.trim(),
      summary: formData.summary ? formData.summary.trim() : '',
      isActive: Boolean(formData.isActive),
    };

    if (selectedCategoryId) {
      payload.categoryId = Number(selectedCategoryId);
      payload.CategoryId = Number(selectedCategoryId);
    }

    try {
      if (selectedItem) {
        await referralLinkAPI.updateReferralLink(payload);
        showToast(t('referralLink.toastUpdated') || 'Referral link updated successfully.', 'success');
      } else {
        await referralLinkAPI.createReferralLink(payload);
        showToast(t('referralLink.toastCreated') || 'Referral link created successfully.', 'success');
      }
      handleCloseDialog();
      fetchReferralLinks();
    } catch (e) {
      console.error('Error saving referral link:', e);
      const errorMsg = e?.response?.data?.errorMessage || e?.message || 'Failed to save referral link.';
      showToast(errorMsg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    const confirmMsg =
      t('referralLink.confirmDelete') ||
      `Are you sure you want to delete the referral link for ${item.moduleKey} (ID: ${item.entityId})?`;
    if (!browserGlobal.confirm(confirmMsg)) return;

    try {
      await referralLinkAPI.deleteReferralLink(item.moduleKey, item.entityId);
      showToast(t('referralLink.toastDeleted') || 'Referral link deleted successfully.', 'success');
      fetchReferralLinks();
    } catch (e) {
      console.error('Error deleting referral link:', e);
      const errorMsg = e?.response?.data?.errorMessage || e?.message || 'Failed to delete referral link.';
      showToast(errorMsg, 'error');
    }
  };

  return (
    <Box p={2}>
      {/* Header Bar */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={1.5}
      >
        <Box>
          <Typography variant="h4" color="primary" fontWeight={700} sx={{ color: '#0F3D3E' }}>
            {t('referralLink.title') || 'Referral Links'}
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={0.5}>
            {t('referralLink.subtitle') ||
              'Manage affiliate & referral links for Contacts, Events, Promotions, and Accommodations.'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '0.92rem',
            textTransform: 'none',
            px: 2.8,
            py: 1,
            borderRadius: '12px',
            boxShadow: '0 4px 14px rgba(15, 61, 62, 0.25)',
            '&:hover': {
              background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              boxShadow: '0 6px 18px rgba(15, 61, 62, 0.35)',
            },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {t('referralLink.add') || 'Add Referral Link'}
        </Button>
      </Box>

      {/* Filters Section (Clean & Compact) */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 1.5,
          bgcolor: '#ffffff',
          borderRadius: 1,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          {/* Module Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="filter-module-label">{t('referralLink.moduleKey') || 'Module'}</InputLabel>
              <Select
                labelId="filter-module-label"
                value={filterModuleKey}
                label={t('referralLink.moduleKey') || 'Module'}
                onChange={(e) => {
                  setFilterModuleKey(e.target.value);
                }}
                sx={{ borderRadius: 1 }}
              >
                {MODULE_OPTIONS.map((mod) => (
                  <MenuItem key={mod} value={mod}>
                    {mod}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Search Query */}
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search label, URL or entity ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1, fontSize: 18 }} />,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
          </Grid>

          {/* Clear Filters */}
          <Grid item xs={12} sm={12} md={3} sx={{ display: 'flex', justifyContent: { md: 'flex-end' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={handleClearFilters}
              sx={{ ...clearFilterButtonSx, borderRadius: 1 }}
            >
              {t('common.clear') || 'Clear'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table / Data View */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" my={8}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : filteredReferralLinks.length === 0 ? (
        <Card
          sx={{
            textAlign: 'center',
            py: 6,
            px: 2,
            borderRadius: 3,
            bgcolor: '#ffffff',
            border: '1px border-dashed #e0e0e0',
            boxShadow: 'none',
          }}
        >
          <CardContent>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#e6f2f2',
                color: '#0F3D3E',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <LinkIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h6" fontWeight={600} color="textPrimary" gutterBottom>
              {t('referralLink.noItems') || 'No Referral Links Found'}
            </Typography>
            <Typography variant="body2" color="textSecondary" maxW={500} mx="auto" mb={3}>
              {t('referralLink.noItemsDesc') || `There are currently no referral links for ${filterModuleKey}. Click the button below to add a new link.`}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(135deg,#1b5a5c 0%,#0a2829 100%)',
                color: '#ffffff',
                borderRadius: 2,
                px: 3,
              }}
            >
              {t('referralLink.create') || t('referralLink.add') || 'Create Referral Link'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid #f0f0f0',
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>
                  {t('referralLink.colTitle') || t('common.title') || 'Title'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>
                  {t('referralLink.colLabel') || t('referralLink.label') || 'Label'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>
                  {t('referralLink.colSummary') || t('referralLink.summary') || 'Summary'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>
                  {t('referralLink.colUrl') || t('referralLink.url') || 'Referral URL'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>
                  {t('referralLink.colOpenCount') || t('referralLink.openCount') || 'Open Count'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>
                  {t('referralLink.colStatus') || t('common.status') || 'Status'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#334155' }}>
                  {t('referralLink.colActions') || t('common.actions') || 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReferralLinks.map((row, index) => (
                <TableRow
                  key={row.id || `${row.moduleKey}-${row.entityId}-${index}`}
                  sx={{
                    '&:hover': { bgcolor: '#f1f5f9' },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="#0F3D3E">
                      {getEntityTitle(row.entityId)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {row.label}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary" noWrap sx={{ maxWidth: 200 }}>
                      {row.summary || row.Summary || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          color: '#2563eb',
                          textDecoration: 'none',
                          maxWidth: 240,
                        }}
                        component="a"
                        href={row.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.url}
                      </Typography>
                      <IconButton
                        size="small"
                        component="a"
                        href={row.url}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ color: '#2563eb', p: 0.5 }}
                      >
                        <OpenInNew fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<TouchApp sx={{ fontSize: '14px !important' }} />}
                      label={`${row.openCount ?? 0} clicks`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: '#cbd5e1',
                        color: '#475569',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {row.isActive ? (
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                        icon={<CheckCircle />}
                        sx={{ fontWeight: 600, borderRadius: 1.5 }}
                      />
                    ) : (
                      <Chip
                        label="Inactive"
                        color="default"
                        size="small"
                        icon={<ErrorOutline />}
                        sx={{ fontWeight: 600, borderRadius: 1.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t('referralLink.edit') || 'Edit Referral Link'}>
                      <IconButton onClick={() => handleOpenDialog(row)} color="primary" size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('referralLink.delete') || 'Delete Referral Link'}>
                      <IconButton onClick={() => handleDelete(row)} color="error" size="small">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Dialog with Dynamic Entity Title Autocomplete & Filters */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0F3D3E', pb: 1 }}>
          {selectedItem ? (t('referralLink.edit') || 'Edit Referral Link') : (t('referralLink.add') || 'Add Referral Link')}
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
            {/* City Select Filter */}
            {cities.length > 0 && (
              <FormControl fullWidth size="small" disabled={Boolean(selectedItem)}>
                <InputLabel>City</InputLabel>
                <Select
                  value={selectedCityId}
                  label="City"
                  disabled={Boolean(selectedItem)}
                  onChange={(e) => {
                    const nextCity = e.target.value;
                    setSelectedCityId(nextCity);
                    setFormSelectedEntity(null);
                    setFormData((prev) => ({ ...prev, entityId: '' }));
                    if (formData.moduleKey === 'Contact') {
                      fetchCategories(nextCity);
                    }
                  }}
                  sx={{ borderRadius: 1.5 }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Module Select */}
            <FormControl fullWidth error={Boolean(errors.moduleKey)} disabled={Boolean(selectedItem)}>
              <InputLabel>{t('referralLink.moduleKey') || 'Module'}</InputLabel>
              <Select
                name="moduleKey"
                value={formData.moduleKey}
                label={t('referralLink.moduleKey') || 'Module'}
                disabled={Boolean(selectedItem)}
                onChange={handleModuleChange}
                sx={{ borderRadius: 1.5 }}
              >
                {MODULE_OPTIONS.map((mod) => (
                  <MenuItem key={mod} value={mod}>
                    {mod}
                  </MenuItem>
                ))}
              </Select>
              {errors.moduleKey && <FormHelperText>{errors.moduleKey}</FormHelperText>}
            </FormControl>

            {/* Category Select (Visible for Contact module) */}
            {formData.moduleKey === 'Contact' && (
              <FormControl fullWidth size="small" disabled={Boolean(selectedItem)}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategoryId}
                  label="Category"
                  disabled={Boolean(selectedItem)}
                  onChange={(e) => {
                    const nextCat = e.target.value;
                    setSelectedCategoryId(nextCat);
                    setFormSelectedEntity(null);
                    setFormData((prev) => ({ ...prev, entityId: '' }));
                  }}
                  sx={{ borderRadius: 1.5 }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((cat) => {
                    const catTitle =
                      cat.title_EN ||
                      cat.title_en ||
                      cat.title ||
                      cat.Title ||
                      cat.name ||
                      cat.Name ||
                      cat.categoryName ||
                      cat.CategoryName ||
                      cat.title_DE ||
                      cat.title_de ||
                      `Category #${cat.id}`;
                    return (
                      <MenuItem key={cat.id} value={cat.id}>
                        {catTitle}
                      </MenuItem>
                    );
                  })}
                </Select>
                {isFetchingCategories ? (
                  <FormHelperText>Loading categories...</FormHelperText>
                ) : null}
              </FormControl>
            )}

            {/* Dynamic Entity Title Picker Autocomplete (Scrollable & Searchable) */}
            <Autocomplete
              options={entities}
              loading={isFetchingEntities}
              disabled={Boolean(selectedItem)}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.title || option.name || option.contactPerson || '';
              }}
              value={formSelectedEntity}
              onChange={(e, newValue) => {
                setFormSelectedEntity(newValue);
                setFormData((prev) => ({
                  ...prev,
                  entityId: newValue ? String(newValue.id) : '',
                }));
                if (errors.entityId) {
                  setErrors((prev) => ({ ...prev, entityId: '' }));
                }
              }}
              ListboxProps={{ style: { maxHeight: 220, overflow: 'auto' } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Title"
                  placeholder="Select title..."
                  required
                  error={Boolean(errors.entityId)}
                  helperText={errors.entityId || undefined}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetchingEntities ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              )}
            />

            {/* Link Label */}
            <TextField
              label={t('referralLink.label') || 'Link Label'}
              name="label"
              value={formData.label}
              onChange={handleChange}
              placeholder="e.g. Book via partner, Get 10% Off"
              required
              error={Boolean(errors.label)}
              helperText={errors.label || undefined}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />

            {/* Summary (Max 50 characters) */}
            <TextField
              label={t('referralLink.summary') || 'Summary'}
              name="summary"
              value={formData.summary || ''}
              onChange={(e) => {
                const val = e.target.value;
                if (val.length <= 50) {
                  setFormData((prev) => ({ ...prev, summary: val }));
                  if (errors.summary) {
                    setErrors((prev) => ({ ...prev, summary: '' }));
                  }
                }
              }}
              placeholder="Brief summary..."
              inputProps={{ maxLength: 50 }}
              error={Boolean(errors.summary)}
              helperText={errors.summary || `${(formData.summary || '').length}/50`}
              FormHelperTextProps={{ sx: { textAlign: 'right' } }}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />

            {/* Referral URL */}
            <TextField
              label={t('referralLink.url') || 'Referral URL'}
              name="url"
              value={formData.url}
              onChange={handleChange}
              placeholder="e.g. https://partner.example.com/ref/abc-123"
              required
              error={Boolean(errors.url)}
              helperText={errors.url || undefined}
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />

            {/* Is Active Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  name="isActive"
                  onChange={handleSwitchChange}
                  color="success"
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  {t('referralLink.isActive') || 'Is Active'}
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit" sx={{ borderRadius: 1.5 }}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              background: 'linear-gradient(135deg,#1b5a5c 0%,#0a2829 100%)',
              color: '#ffffff',
              fontWeight: 600,
              px: 3,
              borderRadius: 1.5,
            }}
          >
            {isSubmitting
              ? (t('common.saving') || 'Saving...')
              : selectedItem
              ? (t('referralLink.update') || 'Update Link')
              : (t('referralLink.create') || t('referralLink.add') || 'Create Link')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={3500}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toast.type || 'success'}
          sx={{
            width: '100%',
            borderRadius: '16px',
            px: 2.5,
            py: 0.8,
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.08)',
            border: toast.type === 'error' ? '1px solid #fecaca' : '1px solid #d1fae5',
            bgcolor: toast.type === 'error' ? '#fef2f2' : '#eefbe7',
            color: toast.type === 'error' ? '#991b1b' : '#054e42',
            fontWeight: 500,
            fontSize: '0.95rem',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              color: toast.type === 'error' ? '#ef4444' : '#10b981',
              fontSize: 22,
            },
            '& .MuiAlert-action': {
              color: toast.type === 'error' ? '#991b1b' : '#054e42',
              pt: 0,
            },
          }}
        >
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReferralLink;
